import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Eye, Download, Mail, MessageCircle } from "lucide-react";
import api, { formatApiError, downloadPdf } from "../lib/api";
import { useAuth, getActiveSchool } from "../context/AuthContext";
import { PageHeader, DataTable, StatusBadge, fmt, waLink } from "../components/bits";
import { SearchSelect } from "../components/SearchSelect";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [students, setStudents] = useState([]);
  const [concepts, setConcepts] = useState([]);
  const [discounts, setDiscounts] = useState([]);
  const [guardians, setGuardians] = useState([]);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [studentId, setStudentId] = useState("");
  const [discountId, setDiscountId] = useState("");
  const [fiscal, setFiscal] = useState(false);
  const [dueDate, setDueDate] = useState("");
  const [items, setItems] = useState([{ _k: "row-0", concept_id: "", description: "", qty: 1, price: 0, student_id: "" }]);
  const [consolidated, setConsolidated] = useState(false);
  const [guardianId, setGuardianId] = useState("");
  const [studentIds, setStudentIds] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [school, setSchool] = useState(null);
  const { user } = useAuth();
  const canDelete = ["superadmin", "admin"].includes(user?.role);

  const load = useCallback(() => {
    const qs = statusFilter === "all" ? "" : `?status=${statusFilter}`;
    api.get(`/invoices${qs}`).then((r) => setInvoices(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [statusFilter, formatApiError]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/students").then((r) => setStudents(r.data.filter((s) => s.status === "activo"))).catch(() => {});
    api.get("/r/concepts").then((r) => setConcepts(r.data.filter((c) => c.active !== false))).catch(() => {});
    api.get("/r/discounts").then((r) => setDiscounts(r.data.filter((d) => d.active !== false))).catch(() => {});
    api.get("/r/guardians").then((r) => setGuardians(r.data)).catch(() => {});
    const sid = getActiveSchool();
    if (sid) api.get(`/schools/${sid}`).then((r) => setSchool(r.data)).catch(() => {});
  }, []);

  const guardianOf = (inv) => guardians.find((g) => g.id === inv.guardian_id);
  const guardianStudents = students.filter((s) => (guardians.find((g) => g.id === guardianId)?.student_ids || []).includes(s.id));

  const setItem = (i, k, v) => {
    const next = [...items];
    next[i] = { ...next[i], [k]: v };
    if (k === "concept_id") {
      const c = concepts.find((x) => x.id === v);
      if (c) { next[i].price = c.price; next[i].description = c.name; }
    }
    setItems(next);
  };

  const subtotal = items.reduce((acc, it) => acc + (parseFloat(it.qty) || 0) * (parseFloat(it.price) || 0), 0);
  const discount = discounts.find((d) => d.id === discountId);
  const discountAmount = discount ? (discount.percent ? subtotal * discount.percent / 100 : Math.min(discount.fixed_amount || 0, subtotal)) : 0;
  const itbis = fiscal ? (subtotal - discountAmount) * 0.18 : 0;
  const total = subtotal - discountAmount + itbis;

  const submit = async () => {
    if (consolidated && (!guardianId || studentIds.length === 0)) { toast.error("Selecciona el tutor y al menos un estudiante"); return; }
    if (!consolidated && !studentId) { toast.error("Selecciona un estudiante"); return; }
    try {
      await api.post("/invoices", {
        student_id: consolidated ? undefined : studentId,
        student_ids: consolidated ? studentIds : undefined,
        guardian_id: consolidated ? guardianId : undefined,
        items: items.filter((it) => it.description).map(({ _k, ...rest }) => rest),
        discount_id: discountId || null,
        fiscal,
        due_date: dueDate,
      });
      toast.success("Factura emitida");
      setOpen(false);
      setItems([{ _k: crypto.randomUUID(), concept_id: "", description: "", qty: 1, price: 0, student_id: "" }]);
      setStudentId(""); setDiscountId(""); setFiscal(false); setDueDate("");
      setConsolidated(false); setGuardianId(""); setStudentIds([]);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (inv) => {
    if (!window.confirm(`¿Eliminar factura ${inv.number}?`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Factura eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendPdf = async (inv) => {
    try {
      await downloadPdf(`/invoices/${inv.id}/pdf`, `${inv.number}.pdf`);
      toast.success(`PDF de ${inv.number} descargado`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendEmail = async (inv) => {
    try {
      const r = await api.post(`/invoices/${inv.id}/email`);
      toast.success(`Factura enviada por correo a ${r.data.to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const sendWhatsApp = (inv) => {
    const g = guardianOf(inv);
    const phone = g?.whatsapp || g?.phone;
    if (!phone) { toast.error("El tutor no tiene teléfono/WhatsApp registrado"); return; }
    const msg = `${inv.number} — Factura de ${inv.student_name}. Total: ${fmt(inv.total)}. Estado: ${inv.status}. Gracias.`;
    window.open(waLink(phone, msg), "_blank");
  };

  const columns = [
    { key: "number", label: "Factura", render: (i) => <span className="font-mono text-xs font-semibold">{i.number}</span> },
    { key: "ncf", label: "NCF", render: (i) => i.ncf ? <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{i.ncf}</span> : <span className="text-xs text-slate-400">Sin valor fiscal</span> },
    { key: "student_name", label: "Estudiante" },
    { key: "issue_date", label: "Fecha" },
    { key: "total", label: "Total", render: (i) => <span className="font-semibold">{fmt(i.total)}</span> },
    { key: "status", label: "Estado", render: (i) => <StatusBadge value={i.status} /> },
    {
      key: "_a", label: "Acciones",
      render: (i) => (
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" title="Ver detalle" data-testid={`view-invoice-${i.id}`} onClick={() => setDetail(i)}>
            <Eye className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" title="Descargar PDF" data-testid={`pdf-invoice-${i.id}`} onClick={() => sendPdf(i)}>
            <Download className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" title="Enviar por correo" data-testid={`email-invoice-${i.id}`} onClick={() => sendEmail(i)}>
            <Mail className="w-4 h-4" />
          </Button>
          <Button size="sm" variant="ghost" title="Enviar por WhatsApp" className="text-emerald-600" data-testid={`whatsapp-invoice-${i.id}`} onClick={() => sendWhatsApp(i)}>
            <MessageCircle className="w-4 h-4" />
          </Button>
          {canDelete && i.status !== "pagada" && (
            <Button size="sm" variant="ghost" className="text-rose-600" title="Eliminar" data-testid={`delete-invoice-${i.id}`} onClick={() => remove(i)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div data-testid="invoices-page">
      <PageHeader title="Facturación" subtitle="Facturas comerciales y fiscales (NCF)">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            <SelectItem value="pendiente">Pendientes</SelectItem>
            <SelectItem value="pagada">Pagadas</SelectItem>
          </SelectContent>
        </Select>
        <Button data-testid="create-invoice-button" className="bg-blue-800 hover:bg-blue-700" onClick={() => setOpen(true)}>
          <Plus className="w-4 h-4 mr-1" /> Nueva factura
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={invoices} testid="invoices-table" searchable searchPlaceholder="Buscar por número, estudiante, NCF…" />

      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="invoice-detail-modal">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  {detail.number}
                  <StatusBadge value={detail.status} />
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {school && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="invoice-issuer">
                    <div className="flex flex-col gap-2">
                      {school.logo_url && (
                        <img src={school.logo_url} alt={school.name} className="h-11 w-auto object-contain rounded self-start" data-testid="invoice-issuer-logo" />
                      )}
                      <div>
                        {(school.show_name !== false || !school.logo_url) && (
                          <p className="font-bold text-slate-900" data-testid="invoice-issuer-name">{school.name}</p>
                        )}
                        <p className="text-xs text-slate-500 font-mono">RNC: {school.rnc || "—"}</p>
                        <p className="text-xs text-slate-500">{school.address}</p>
                        <p className="text-xs text-slate-500">Tel: {(school.phones || []).join(", ")}</p>
                      </div>
                    </div>
                  </div>
                )}
                {detail.ncf && <p className="font-mono text-xs text-emerald-700">NCF: {detail.ncf}</p>}
                <div className="grid grid-cols-2 gap-2 text-slate-600">
                  <p><span className="text-slate-400">Estudiante:</span> {detail.student_name}</p>
                  <p><span className="text-slate-400">Tutor:</span> {detail.guardian_name || "—"}</p>
                  <p><span className="text-slate-400">Fecha:</span> {detail.issue_date}</p>
                  <p><span className="text-slate-400">Vence:</span> {detail.due_date || "—"}</p>
                </div>
                <table className="w-full text-xs border border-slate-200 rounded-lg overflow-hidden">
                  <thead className="bg-slate-50">
                    <tr><th className="text-left px-3 py-2">Descripción</th><th className="px-3 py-2">Cant.</th><th className="text-right px-3 py-2">Importe</th></tr>
                  </thead>
                  <tbody>
                    {detail.items.map((it, idx) => (
                      <tr key={`${idx}-${it.description}-${it.amount}`} className="border-t border-slate-100">
                        <td className="px-3 py-2">{it.description}{it.student_name ? <span className="text-slate-400"> · {it.student_name}</span> : null}</td>
                        <td className="px-3 py-2 text-center">{it.qty}</td>
                        <td className="px-3 py-2 text-right">{fmt(it.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="space-y-1 text-right text-sm">
                  <p className="text-slate-500">Subtotal: {fmt(detail.subtotal)}</p>
                  {detail.discount_amount > 0 && <p className="text-slate-500">Descuento ({detail.discount_name}): -{fmt(detail.discount_amount)}</p>}
                  {detail.itbis > 0 && <p className="text-slate-500">ITBIS (18%): {fmt(detail.itbis)}</p>}
                  <p className="font-bold text-slate-900 text-base" data-testid="detail-total">Total: {fmt(detail.total)}</p>
                </div>
                <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
                  <Button size="sm" variant="outline" data-testid="detail-pdf" onClick={() => sendPdf(detail)}><Download className="w-4 h-4 mr-1" /> PDF</Button>
                  <Button size="sm" variant="outline" data-testid="detail-email" onClick={() => sendEmail(detail)}><Mail className="w-4 h-4 mr-1" /> Correo</Button>
                  <Button size="sm" variant="outline" className="text-emerald-600" data-testid="detail-whatsapp" onClick={() => sendWhatsApp(detail)}><MessageCircle className="w-4 h-4 mr-1" /> WhatsApp</Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva factura</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-3 bg-sky-50 border border-sky-200 rounded-xl px-4 py-3">
              <Switch
                data-testid="invoice-consolidated-switch"
                checked={consolidated}
                onCheckedChange={(v) => { setConsolidated(v); setStudentId(""); setGuardianId(""); setStudentIds([]); }}
              />
              <Label className="!mt-0">Factura consolidada por tutor (varios estudiantes en una sola factura)</Label>
            </div>
            {!consolidated ? (
              <div className="space-y-1.5">
                <Label>Estudiante</Label>
                <SearchSelect
                  testid="invoice-student-select"
                  placeholder="Buscar estudiante…"
                  value={studentId}
                  onChange={setStudentId}
                  options={students.map((s) => ({ value: s.id, label: `${s.first_name} ${s.last_name} — ${s.grade}` }))}
                />
              </div>
            ) : (
              <>
                <div className="space-y-1.5">
                  <Label>Tutor (la factura se emite a su nombre)</Label>
                  <SearchSelect
                    testid="invoice-guardian-select"
                    placeholder="Buscar tutor por nombre o cédula…"
                    value={guardianId}
                    onChange={(v) => { setGuardianId(v); setStudentIds([]); }}
                    options={guardians.map((g) => ({ value: g.id, label: `${g.name} ${g.last_name} — ${g.document}` }))}
                  />
                </div>
                {guardianId && (
                  <div className="space-y-1.5">
                    <Label>Estudiantes a consolidar ({studentIds.length} seleccionados)</Label>
                    <div className="border border-slate-200 rounded-lg p-3 space-y-2 max-h-36 overflow-y-auto" data-testid="invoice-students-picker">
                      {guardianStudents.map((s) => {
                        const checked = studentIds.includes(s.id);
                        return (
                          <label key={s.id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" data-testid={`invoice-student-check-${s.id}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => setStudentIds(checked ? studentIds.filter((x) => x !== s.id) : [...studentIds, s.id])}
                            />
                            {s.first_name} {s.last_name} — {s.grade}
                          </label>
                        );
                      })}
                      {guardianStudents.length === 0 && <p className="text-xs text-slate-400">Este tutor no tiene estudiantes vinculados</p>}
                    </div>
                  </div>
                )}
              </>
            )}
            <div className="space-y-2">
              <Label>Conceptos</Label>
              {items.map((it, i) => (
                <div key={it._k} className={`grid ${consolidated ? "grid-cols-[130px_1fr_70px_110px_36px]" : "grid-cols-[1fr_70px_110px_36px]"} gap-2 items-center`}>
                  {consolidated && (
                    <Select value={it.student_id || "_all"} onValueChange={(v) => setItem(i, "student_id", v === "_all" ? "" : v)}>
                      <SelectTrigger data-testid={`invoice-item-student-${i}`}><SelectValue placeholder="Todos" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_all">Todos</SelectItem>
                        {students.filter((s) => studentIds.includes(s.id)).map((s) => <SelectItem key={s.id} value={s.id}>{s.first_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                  <Select value={it.concept_id} onValueChange={(v) => setItem(i, "concept_id", v)}>
                    <SelectTrigger data-testid={`invoice-item-concept-${i}`}><SelectValue placeholder="Concepto" /></SelectTrigger>
                    <SelectContent>{concepts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" min="1" data-testid={`invoice-item-qty-${i}`} value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} />
                  <Input type="number" data-testid={`invoice-item-price-${i}`} value={it.price} onChange={(e) => setItem(i, "price", e.target.value)} />
                  <Button size="sm" variant="ghost" className="text-rose-600" onClick={() => setItems(items.filter((_, x) => x !== i))} disabled={items.length === 1}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" data-testid="add-invoice-item" onClick={() => setItems([...items, { _k: crypto.randomUUID(), concept_id: "", description: "", qty: 1, price: 0, student_id: "" }])}>
                <Plus className="w-3 h-3 mr-1" /> Agregar concepto
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Descuento</Label>
                <Select value={discountId || "_none"} onValueChange={(v) => setDiscountId(v === "_none" ? "" : v)}>
                  <SelectTrigger data-testid="invoice-discount-select"><SelectValue placeholder="Sin descuento" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Sin descuento</SelectItem>
                    {discounts.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} {d.percent ? `(${d.percent}%)` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Vencimiento</Label><Input type="date" data-testid="invoice-due-date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
            </div>
            <div className="flex items-center gap-3">
              <Switch data-testid="invoice-fiscal-switch" checked={fiscal} onCheckedChange={setFiscal} />
              <Label className="!mt-0">Factura fiscal (con NCF e ITBIS 18%)</Label>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm space-y-1">
              <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span data-testid="invoice-subtotal">{fmt(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Descuento {discount ? `(${discount.name})` : ""}</span><span>-{fmt(discountAmount)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">ITBIS (18%)</span><span>{fmt(itbis)}</span></div>
              <div className="flex justify-between font-bold text-slate-900 border-t border-slate-200 pt-1"><span>Total</span><span data-testid="invoice-total">{fmt(total)}</span></div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-invoice" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-invoice" className="bg-blue-800 hover:bg-blue-700" onClick={submit}>Emitir factura</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
